import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Switch from '@mui/material/Switch';
import { styled } from '@mui/material/styles';
import * as React from 'react';

import { useAISwitchStore } from './aiSwitchStore';

const MaterialUISwitchAI = styled(Switch)(({ theme }) => ({
  width: 48,
  height: 26,
  padding: 7,
  '& .MuiSwitch-switchBase': {
    margin: 3,
    padding: 0,
    transform: 'translateX(0)',
    '&.Mui-checked': {
      color: theme.palette.blue[600],
      transform: 'translateX(22px)',
      '& .MuiSwitch-thumb:before': {
        backgroundImage: `url(/images/ai-on.svg)`,
      },
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.blue[600],
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 20,
    '&::before': {
      content: "''",
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundImage: `url(/images/ai-off.svg)`,
    },
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    backgroundColor: theme.palette.grey[400],
    borderRadius: 10,
  },
}));

type AISwitchToggleProps = {
  isChecked?: boolean;
  onChange?: (isChecked: boolean) => void;
  sessionStorageEnabled?: boolean;
};

export default function AISwitchToggle({
  isChecked,
  onChange,
  sessionStorageEnabled = false,
}: Readonly<AISwitchToggleProps>) {
  const storedAISwitchState = useAISwitchStore((s) => s.isAISwitchOn);
  const setIsAISwitchOn = useAISwitchStore((s) => s.setIsAISwitchOn);

  const isControlled = isChecked !== undefined;
  const isAISwitchOn = isControlled ? isChecked : storedAISwitchState;

  React.useEffect(() => {
    if (sessionStorageEnabled) {
      sessionStorage.setItem('isAISwitchOn', `${isAISwitchOn}`);
      window.postMessage({ isAISwitchOn }, globalThis.location.origin);
    }
  }, [isAISwitchOn, sessionStorageEnabled]);

  const handleSwitchChange: React.ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    if (isControlled) {
      onChange?.(e.target.checked);
    } else {
      setIsAISwitchOn(e.target.checked);
    }
  };

  return (
    <FormGroup>
      <FormControlLabel
        label=""
        control={
          <MaterialUISwitchAI
            data-testid="ai-switch-toggle-button"
            slotProps={{ input: { role: 'switch' } }}
            aria-label="Toggle AI"
            sx={{ m: 1, marginLeft: '1rem' }}
            checked={isAISwitchOn}
            onChange={handleSwitchChange}
          />
        }
      />
    </FormGroup>
  );
}
